import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpAdmin } from '../lib/http';
import { useGuard401 } from './adminShared';
import './AdminHealth.css';

const SERVICE_ORDER = ['api', 'database', 'auth', 'notifications', 'smtp', 's3', 'redis'];
const SERVICE_LABELS = {
  api: 'API',
  database: 'Database',
  auth: 'Auth',
  notifications: 'Notifications',
  smtp: 'SMTP',
  s3: 'S3',
  redis: 'Redis',
};
const SERVICE_EMOJI = {
  api: '🖥️',
  database: '🗄️',
  auth: '🔐',
  notifications: '🔔',
  smtp: '📧',
  s3: '☁️',
  redis: '⚡',
};

function statusTone(status) {
  if (status === 'healthy') return 'ok';
  if (status === 'unhealthy') return 'bad';
  if (status === 'not_configured') return 'muted';
  if (status === 'checking') return 'warn';
  return 'muted';
}

function statusEmoji(status) {
  if (status === 'healthy') return '✅';
  if (status === 'unhealthy' || status === 'degraded') return '❌';
  if (status === 'not_configured') return '⚪';
  if (status === 'checking') return '⏳';
  return '❓';
}

function statusLabel(status) {
  if (status === 'not_configured') return 'Not configured';
  if (status === 'degraded') return 'Degraded';
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function Sparkline({ points }) {
  const values = (points || []).map((p) => Number(p.rttMs) || 0).filter((n) => n >= 0);
  if (values.length < 2) {
    return <div className="adm-health-spark adm-health-spark--empty" aria-hidden="true" />;
  }
  const w = 120;
  const h = 28;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  const coords = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / span) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg className="adm-health-spark" viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden="true">
      <polyline fill="none" stroke="currentColor" strokeWidth="2" points={coords} />
    </svg>
  );
}

const emptyAlerts = {
  enabled: false,
  email: '',
  webhookUrl: '',
  degradeMinutes: 5,
  cooldownMinutes: 30,
};

export default function AdminHealth() {
  const guard = useGuard401();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [rowBusy, setRowBusy] = useState(null);
  const [copied, setCopied] = useState(false);
  const [alerts, setAlerts] = useState(emptyAlerts);
  const [alertsSaving, setAlertsSaving] = useState(false);
  const [alertsMsg, setAlertsMsg] = useState('');

  const load = useCallback(async ({ soft = false } = {}) => {
    if (soft) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const data = await httpAdmin.get('/api/admin/health');
      setSnapshot(data);
      if (data?.alerts) {
        setAlerts({
          enabled: Boolean(data.alerts.enabled),
          email: data.alerts.email || '',
          webhookUrl: data.alerts.webhookUrl || '',
          degradeMinutes: data.alerts.degradeMinutes || 5,
          cooldownMinutes: data.alerts.cooldownMinutes || 30,
        });
      }
    } catch (err) {
      if (!guard(err)) setError(err.message || 'Failed to load system health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [guard]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => load({ soft: true }), refreshInterval * 1000);
    return () => clearInterval(id);
  }, [autoRefresh, refreshInterval, load]);

  const recheckService = async (service) => {
    setRowBusy(service);
    try {
      const data = await httpAdmin.get(`/api/admin/health?service=${encodeURIComponent(service)}`);
      const next = data?.services?.[service];
      if (next) {
        setSnapshot((prev) => {
          if (!prev) return data;
          const services = { ...prev.services, [service]: next };
          const unhealthy = Object.values(services).filter((s) => s.status === 'unhealthy').length;
          const healthy = Object.values(services).filter((s) => s.status === 'healthy').length;
          const overall = unhealthy ? 'degraded' : 'healthy';
          return {
            ...prev,
            ...data,
            services,
            overall,
            performance: {
              ...prev.performance,
              ...data.performance,
              healthyCount: healthy,
              unhealthyCount: unhealthy,
              monitoredCount: Object.keys(services).length,
            },
            history: data.history || prev.history,
          };
        });
      }
    } catch (err) {
      if (!guard(err)) setError(err.message || `Failed to recheck ${service}`);
    } finally {
      setRowBusy(null);
    }
  };

  const copyDebug = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setError('Could not copy debug JSON');
    }
  };

  const saveAlerts = async (event) => {
    event.preventDefault();
    setAlertsSaving(true);
    setAlertsMsg('');
    try {
      const res = await httpAdmin.put('/api/admin/health/alerts', alerts);
      setAlerts({
        enabled: Boolean(res?.alerts?.enabled),
        email: res?.alerts?.email || '',
        webhookUrl: res?.alerts?.webhookUrl || '',
        degradeMinutes: res?.alerts?.degradeMinutes || 5,
        cooldownMinutes: res?.alerts?.cooldownMinutes || 30,
      });
      setAlertsMsg('Alert settings saved');
    } catch (err) {
      if (!guard(err)) setError(err.message || 'Failed to save alerts');
    } finally {
      setAlertsSaving(false);
    }
  };

  const services = useMemo(() => {
    const map = snapshot?.services || {};
    return SERVICE_ORDER.filter((key) => map[key]).map((key) => ({ key, ...map[key] }));
  }, [snapshot]);

  const issues = services.filter((s) => s.status === 'unhealthy');
  const overall = snapshot?.overall || 'degraded';
  const system = snapshot?.system;

  return (
    <section className="adm-page adm-page--health">
      <header className="adm-page-head adm-health-head">
        <div>
          <h2><span aria-hidden="true">💚</span> System Health</h2>
          <p className="adm-health-desc">
            Live probes for API, data, auth, notifications, and platform dependencies.
          </p>
        </div>
      </header>

      <div className="adm-health-tools" aria-label="Health controls">
        <label className="adm-health-auto">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <span aria-hidden="true">🔁</span> Auto refresh
        </label>
        <select
          className="adm-select"
          value={refreshInterval}
          onChange={(e) => setRefreshInterval(Number(e.target.value))}
          disabled={!autoRefresh}
          aria-label="Refresh interval"
        >
          <option value={10}>10s</option>
          <option value={30}>30s</option>
          <option value={60}>1m</option>
          <option value={300}>5m</option>
        </select>
        <button type="button" className="adm-health-refresh" onClick={() => load({ soft: true })} disabled={refreshing || loading}>
          <span aria-hidden="true">{refreshing ? '⏳' : '🔄'}</span>
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>

      {error && (
        <div className="adm-error" role="alert">
          <span><span aria-hidden="true">⚠️</span> {error}</span>
          <button type="button" className="btn-sm btn-secondary" onClick={() => load()}>Retry</button>
        </div>
      )}

      {loading && !snapshot ? (
        <div className="adm-loading"><span aria-hidden="true">⏳</span> Checking system health…</div>
      ) : (
        <>
          <section className={`adm-health-banner adm-health-banner--${overall === 'healthy' ? 'ok' : 'bad'}`} aria-label="Overall status">
            <div className="adm-health-banner__main">
              <span className="adm-health-banner__emoji" aria-hidden="true">{statusEmoji(overall)}</span>
              <div>
                <div className="adm-health-banner__title-row">
                  <span className={`adm-health-pill adm-health-pill--${statusTone(overall)}`}>
                    <span aria-hidden="true">{statusEmoji(overall)}</span>
                    {overall === 'healthy' ? 'Healthy' : 'Degraded'}
                  </span>
                </div>
                <h3>{overall === 'healthy' ? 'All critical probes are healthy' : 'One or more services need attention'}</h3>
                <p>
                  <span aria-hidden="true">🕒</span>{' '}
                  Last checked {snapshot?.checkedAt ? new Date(snapshot.checkedAt).toLocaleString() : '—'}
                  {refreshing ? ' · updating…' : ''}
                </p>
              </div>
            </div>
            <div className="adm-health-banner__meta">
              <div>
                <span className="adm-health-meta-label"><span aria-hidden="true">⏱️</span> Uptime</span>
                <strong>{system?.uptime || '—'}</strong>
              </div>
              <div>
                <span className="adm-health-meta-label"><span aria-hidden="true">🧠</span> Memory (RSS)</span>
                <strong>{system?.memory?.rssMb != null ? `${system.memory.rssMb} MB` : '—'}</strong>
              </div>
              <div>
                <span className="adm-health-meta-label"><span aria-hidden="true">📦</span> Heap used</span>
                <strong>{system?.memory?.heapUsedMb != null ? `${system.memory.heapUsedMb} MB` : '—'}</strong>
              </div>
              <div>
                <span className="adm-health-meta-label"><span aria-hidden="true">🟢</span> Node</span>
                <strong>{system?.node || '—'}</strong>
              </div>
            </div>
          </section>

          <section className="adm-health-section" aria-labelledby="health-services-title">
            <header className="adm-health-section__head">
              <h3 id="health-services-title"><span aria-hidden="true">🧩</span> Services</h3>
              <p><span aria-hidden="true">📡</span> Client RTT = round-trip time from this browser to each probe.</p>
            </header>
            <div className="adm-health-legend" aria-label="Status legend">
              <span><span aria-hidden="true">✅</span> Healthy</span>
              <span><span aria-hidden="true">❌</span> Unhealthy</span>
              <span><span aria-hidden="true">⚪</span> Not configured</span>
              <span><span aria-hidden="true">⏳</span> Checking</span>
            </div>
            <div className="adm-health-services">
              {services.map((service) => {
                const series = snapshot?.history?.byService?.[service.key] || [];
                return (
                  <article key={service.key} className={`adm-health-tile adm-health-tile--${statusTone(service.status)}`}>
                    <div className="adm-health-tile__top">
                      <h4>
                        <span className="adm-health-tile__emoji" aria-hidden="true">{SERVICE_EMOJI[service.key] || '🔹'}</span>
                        {SERVICE_LABELS[service.key] || service.key}
                      </h4>
                      <span className={`adm-health-pill adm-health-pill--${statusTone(service.status)}`}>
                        <span aria-hidden="true">{statusEmoji(service.status)}</span>
                        {statusLabel(service.status)}
                      </span>
                    </div>
                    <p className="adm-health-tile__msg">{service.message || '—'}</p>
                    <div className="adm-health-tile__metrics">
                      <div>
                        <span className="adm-health-meta-label"><span aria-hidden="true">📡</span> RTT</span>
                        <strong>{service.rttMs != null ? `${service.rttMs} ms` : '—'}</strong>
                      </div>
                      <div className="adm-health-tile__spark-wrap" title="24h RTT sparkline">
                        <span className="adm-health-meta-label"><span aria-hidden="true">📈</span> 24h</span>
                        <Sparkline points={series} />
                      </div>
                    </div>
                    {service.lastFailure && (
                      <p className="adm-health-tile__failure">
                        <span aria-hidden="true">💥</span> Last failure {new Date(service.lastFailure.at).toLocaleString()}: {service.lastFailure.error}
                      </p>
                    )}
                    {service.error && service.status === 'unhealthy' && (
                      <p className="adm-health-tile__error"><span aria-hidden="true">⚠️</span> {service.error}</p>
                    )}
                    <button
                      type="button"
                      className="adm-health-tile__recheck"
                      disabled={rowBusy === service.key}
                      onClick={() => recheckService(service.key)}
                    >
                      <span aria-hidden="true">{rowBusy === service.key ? '⏳' : '🔄'}</span>
                      {rowBusy === service.key ? 'Checking…' : 'Recheck'}
                    </button>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="adm-health-section" aria-labelledby="health-perf-title">
            <header className="adm-health-section__head">
              <h3 id="health-perf-title"><span aria-hidden="true">⚡</span> Performance</h3>
              <p>Aggregate client RTT across the latest full probe set.</p>
            </header>
            <div className="adm-health-perf">
              <div className="adm-health-perf__card">
                <span className="adm-health-meta-label"><span aria-hidden="true">📡</span> Average RTT</span>
                <strong>{snapshot?.performance?.averageRttMs ?? 0} ms</strong>
              </div>
              <div className="adm-health-perf__card">
                <span className="adm-health-meta-label"><span aria-hidden="true">🧮</span> Total RTT</span>
                <strong>{snapshot?.performance?.totalRttMs ?? 0} ms</strong>
              </div>
              <div className="adm-health-perf__card">
                <span className="adm-health-meta-label"><span aria-hidden="true">✅</span> Healthy</span>
                <strong>{snapshot?.performance?.healthyCount ?? 0}</strong>
              </div>
              <div className="adm-health-perf__card">
                <span className="adm-health-meta-label"><span aria-hidden="true">❌</span> Unhealthy</span>
                <strong>{snapshot?.performance?.unhealthyCount ?? 0}</strong>
              </div>
              <div className="adm-health-perf__card">
                <span className="adm-health-meta-label"><span aria-hidden="true">⚪</span> Not configured</span>
                <strong>{snapshot?.performance?.notConfiguredCount ?? 0}</strong>
              </div>
              <div className="adm-health-perf__card">
                <span className="adm-health-meta-label"><span aria-hidden="true">📊</span> 24h samples</span>
                <strong>{snapshot?.history?.sampleCount ?? 0}</strong>
              </div>
            </div>
          </section>

          <section className="adm-health-section" aria-labelledby="health-issues-title">
            <header className="adm-health-section__head">
              <h3 id="health-issues-title">
                <span aria-hidden="true">{issues.length ? '🚨' : '🎉'}</span>{' '}
                {issues.length ? 'Issues' : 'All clear'}
              </h3>
              <p>{issues.length ? 'Services currently reporting unhealthy.' : 'No active service failures.'}</p>
            </header>
            {issues.length ? (
              <div className="adm-health-issues">
                {issues.map((issue) => (
                  <div key={issue.key} className="adm-health-issue">
                    <strong>
                      <span aria-hidden="true">{SERVICE_EMOJI[issue.key] || '🔹'}</span>{' '}
                      {SERVICE_LABELS[issue.key] || issue.key}
                    </strong>
                    <span><span aria-hidden="true">⚠️</span> {issue.error || issue.message}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="adm-health-success">
                <span className="adm-health-success__emoji" aria-hidden="true">🥳</span>
                <div>
                  <span className="adm-health-pill adm-health-pill--ok">
                    <span aria-hidden="true">✅</span> All clear
                  </span>
                  <p>Critical probes are healthy. Optional dependencies may still show as not configured.</p>
                </div>
              </div>
            )}
          </section>

          <section className="adm-health-section" aria-labelledby="health-alerts-title">
            <header className="adm-health-section__head">
              <h3 id="health-alerts-title"><span aria-hidden="true">🚨</span> Alerts</h3>
              <p>Saved in admin Settings (`healthAlerts`). Email and/or webhook when degraded past the threshold.</p>
            </header>
            <form className="adm-health-alerts" onSubmit={saveAlerts}>
              <label className="adm-health-auto">
                <input
                  type="checkbox"
                  checked={alerts.enabled}
                  onChange={(e) => setAlerts((a) => ({ ...a, enabled: e.target.checked }))}
                />
                <span aria-hidden="true">🔔</span> Enable degrade alerts
              </label>
              <label>
                <span aria-hidden="true">📧</span> Alert email
                <input
                  type="email"
                  value={alerts.email}
                  onChange={(e) => setAlerts((a) => ({ ...a, email: e.target.value }))}
                  placeholder="ops@example.com"
                />
              </label>
              <label>
                <span aria-hidden="true">🔗</span> Webhook URL
                <input
                  type="url"
                  value={alerts.webhookUrl}
                  onChange={(e) => setAlerts((a) => ({ ...a, webhookUrl: e.target.value }))}
                  placeholder="https://hooks.example.com/…"
                />
              </label>
              <label>
                Degrade threshold (minutes)
                <input
                  type="number"
                  min={1}
                  max={120}
                  value={alerts.degradeMinutes}
                  onChange={(e) => setAlerts((a) => ({ ...a, degradeMinutes: Number(e.target.value) || 5 }))}
                />
              </label>
              <label>
                Cooldown (minutes)
                <input
                  type="number"
                  min={5}
                  max={1440}
                  value={alerts.cooldownMinutes}
                  onChange={(e) => setAlerts((a) => ({ ...a, cooldownMinutes: Number(e.target.value) || 30 }))}
                />
              </label>
              <div className="adm-health-alerts__actions">
                <button type="submit" className="submit-button" disabled={alertsSaving}>
                  {alertsSaving ? 'Saving…' : 'Save alert settings'}
                </button>
                {alertsMsg && <span className="adm-health-alerts__ok">{alertsMsg}</span>}
              </div>
            </form>
          </section>

          <section className="adm-health-section adm-health-diagnostics" aria-labelledby="health-diag-title">
            <header className="adm-health-section__head">
              <h3 id="health-diag-title"><span aria-hidden="true">🧪</span> Diagnostics</h3>
              <p>Raw snapshot for support. Copy before sharing logs.</p>
            </header>
            <div className="adm-health-diag-bar">
              <button type="button" className="btn-secondary" onClick={copyDebug}>
                <span aria-hidden="true">{copied ? '✅' : '📋'}</span>
                {copied ? 'Copied' : 'Copy debug JSON'}
              </button>
            </div>
            <details className="adm-health-details">
              <summary><span aria-hidden="true">📄</span> Show debug JSON</summary>
              <pre>{JSON.stringify(snapshot, null, 2)}</pre>
            </details>
          </section>
        </>
      )}
    </section>
  );
}
